import { AppState, AppStateStatus, NativeEventSubscription } from 'react-native';

export type ForegroundHandler = (previousState: AppStateStatus) => void;
export type BackgroundHandler = (nextState: AppStateStatus) => void;

export interface AppStateForegroundTrackerOptions {
  onForeground?: ForegroundHandler;
  onBackground?: BackgroundHandler;
  debounceMs?: number;
  name?: string;
}

export class AppStateForegroundTracker {
  private appState: AppStateStatus = AppState.currentState;
  private subscription: NativeEventSubscription | null = null;
  private lastForegroundAt = 0;
  private readonly debounceMs: number;
  private readonly onForeground?: ForegroundHandler;
  private readonly onBackground?: BackgroundHandler;
  private readonly name: string;

  constructor(options: AppStateForegroundTrackerOptions = {}) {
    this.onForeground = options.onForeground;
    this.onBackground = options.onBackground;
    this.debounceMs = options.debounceMs ?? 0;
    this.name = options.name ?? 'AppStateTracker';
  }

  start(): void {
    if (this.subscription) return;
    this.subscription = AppState.addEventListener('change', this.handleChange);
  }

  stop(): void {
    if (!this.subscription) return;
    this.subscription.remove();
    this.subscription = null;
  }

  getCurrentState(): AppStateStatus {
    return this.appState;
  }

  isActive(): boolean {
    return this.appState === 'active';
  }

  private handleChange = (nextAppState: AppStateStatus): void => {
    const previous = this.appState;
    this.appState = nextAppState;

    const becameForeground = previous.match(/inactive|background/) && nextAppState === 'active';
    const becameBackground = nextAppState.match(/inactive|background/) && previous === 'active';

    if (becameForeground) {
      const now = Date.now();
      if (this.debounceMs > 0 && now - this.lastForegroundAt < this.debounceMs) {
        console.log(`[${this.name}] foreground debounced`);
        return;
      }
      this.lastForegroundAt = now;
      try {
        this.onForeground?.(previous);
      } catch (error) {
        console.error(`[${this.name}] onForeground handler error:`, error);
      }
    } else if (becameBackground) {
      try {
        this.onBackground?.(nextAppState);
      } catch (error) {
        console.error(`[${this.name}] onBackground handler error:`, error);
      }
    }
  };
}
